import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { CostOverrideType } from '../CostOverrideType';

const excludeById = id => d => d.id !== id;

const buildEmptyDimensions = dimensions =>
  dimensions.reduce(
    (acc, d) => ({ ...acc, [d.id]: { id: uuid(), value: null } }),
    {}
  );

const buildSubEntryDimensions = (dimensions, dimensionIndex, record) =>
  dimensions.reduce(
    (acc, d, i) => ({
      ...acc,
      [d.id]: i < dimensionIndex ? record[d.id] : { id: uuid(), value: null }
    }),
    {}
  );

const createTopLevelEntry = (dimensions, currency) => ({
  ...buildEmptyDimensions(dimensions),
  effectiveDate: null,
  amount: { amount: 0, currency },
  percent: 0,
  type: CostOverrideType.HOURLY_RATE,
  groupId: uuid(),
  id: uuid(),
  isInitialRate: true,
  totalEntries: 1
});

const createSubEntry = (dimensions, currency, dimensionIndex, record) => ({
  ...buildSubEntryDimensions(dimensions, dimensionIndex, record),
  effectiveDate: null,
  amount: { amount: 0, currency },
  percent: 0,
  type: CostOverrideType.HOURLY_RATE,
  groupId: uuid(),
  id: uuid(),
  isInitialRate: true,
  totalEntries: 1
});

const addEmptyDimensionToRates = (rates, dimension) =>
  rates.map(r => ({
    ...r,
    [dimension.id]: { id: uuid(), value: null }
  }));

const stripDimensionFromRate = (rate, dimensionId) => {
  const next = { ...rate };

  delete next[dimensionId];

  return next;
};

const stripDimensionFromAllRates = (rates, dimensionId) =>
  rates.map(r => stripDimensionFromRate(r, dimensionId));

const migrateDimensionValue = (
  rates,
  target,
  dimensionId,
  dimensionIndex,
  value,
  selectedDimensions
) => {
  const dimensionsToMatch = selectedDimensions.slice(0, dimensionIndex + 1);
  const valueIdAt = (r, d) => r[d.id]?.value?.id ?? null;
  const cellIdAt = (r, d) => r[d.id]?.id ?? null;
  // Two matching strategies are needed because reconcileWorkingWithServer may
  // have replaced `target` with a fresh server row before this runs. When the
  // exact target object is still in `rates` (or its cell has no value yet, so
  // there is no value id to match on) we match sibling rows by their unique
  // cell id. Otherwise the target was swapped out and cell ids may differ, so
  // we fall back to matching by dimension value id.
  const canMatchByCellId = rates.includes(target);
  const matchesTargetAt = (r, d) =>
    canMatchByCellId || valueIdAt(target, d) === null
      ? cellIdAt(r, d) === cellIdAt(target, d)
      : valueIdAt(r, d) === valueIdAt(target, d);
  const sharesDimensionsWithTarget = r =>
    dimensionsToMatch.every(d => matchesTargetAt(r, d));

  return rates.map(r =>
    sharesDimensionsWithTarget(r)
      ? { ...r, [dimensionId]: { ...r[dimensionId], value } }
      : r
  );
};

const setEffectiveDateAt = (rates, targetIndex, value) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, effectiveDate: value } : r));

const setAmountAt = (rates, targetIndex, value) =>
  rates.map((r, i) =>
    i === targetIndex ? { ...r, amount: { ...r.amount, amount: value } } : r
  );

const setCurrencyAt = (rates, targetIndex, value) =>
  rates.map((r, i) =>
    i === targetIndex ? { ...r, amount: { ...r.amount, currency: value } } : r
  );

const setTypeAt = (rates, targetIndex, type) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, type } : r));

const setPercentAt = (rates, targetIndex, value) =>
  rates.map((r, i) => (i === targetIndex ? { ...r, percent: value } : r));

const adjustGroupTotals = (rates, groupId, delta) =>
  rates.map(r =>
    r.groupId === groupId ? { ...r, totalEntries: r.totalEntries + delta } : r
  );

const removeRateAtIndex = (rates, index) => rates.filter((_, i) => i !== index);

const removeRatesByGroupId = (rates, groupId) =>
  rates.filter(r => r.groupId !== groupId);

const insertEntryAt = (rates, atIndex, entry) => {
  const next = [...rates];

  next.splice(atIndex, 0, entry);

  return next;
};

const insertSubEntry = (rates, entry, projectCurrency) => {
  const firstEntryIndex = rates.findIndex(e => e.groupId === entry.groupId);
  const inserted = insertEntryAt(rates, firstEntryIndex, {
    ...entry,
    id: uuid(),
    amount: { amount: 0, currency: projectCurrency },
    percent: 0,
    type: CostOverrideType.HOURLY_RATE,
    isInitialRate: false
  });

  return adjustGroupTotals(inserted, entry.groupId, 1);
};

const removeAndAdjustGroup = (rates, index, entry) => {
  const next = removeRateAtIndex(rates, index);

  return adjustGroupTotals(next, entry.groupId, -1);
};

const rowSignatureFor = selectedDimensions => r =>
  selectedDimensions.map(d => r[d.id]?.value?.id ?? '').join('|');

const hasAnyDimensionValue = selectedDimensions => r =>
  selectedDimensions.some(d => r[d.id]?.value?.id);

const sharedPrefixDepth = (rowA, rowB, selectedDimensions) => {
  let depth = 0;

  for (let k = 0; k < selectedDimensions.length; k += 1) {
    const value = rowA[selectedDimensions[k].id]?.value?.id;

    if (!value || rowB[selectedDimensions[k].id]?.value?.id !== value) break;
    depth += 1;
  }

  return depth;
};

const alignRowToAnchor = (row, anchor, depth, selectedDimensions) => {
  let aligned = row;

  for (let k = 0; k < depth; k += 1) {
    const { id } = selectedDimensions[k];

    if (aligned[id] && anchor[id] && aligned[id].id !== anchor[id].id) {
      aligned = { ...aligned, [id]: { ...aligned[id], id: anchor[id].id } };
    }
  }

  if (
    depth === selectedDimensions.length &&
    aligned.groupId !== anchor.groupId
  ) {
    aligned = { ...aligned, groupId: anchor.groupId };
  }

  return aligned;
};

const stitchNewlyLoadedIntoWorking = (
  working,
  newlyLoaded,
  selectedDimensions
) => {
  if (newlyLoaded.length === 0) return working;

  const insertionsAfter = new Map();
  const appended = [];

  newlyLoaded.forEach(row => {
    let anchorIndex = -1;
    let anchor = null;
    let bestDepth = 0;

    working.forEach((workingRow, i) => {
      if (!workingRow.isPersisted) return;

      const depth = sharedPrefixDepth(row, workingRow, selectedDimensions);

      if (depth > 0 && depth >= bestDepth) {
        bestDepth = depth;
        anchorIndex = i;
        anchor = workingRow;
      }
    });

    if (anchorIndex === -1) {
      appended.push(row);

      return;
    }

    const aligned = alignRowToAnchor(
      row,
      anchor,
      bestDepth,
      selectedDimensions
    );

    if (!insertionsAfter.has(anchorIndex)) {
      insertionsAfter.set(anchorIndex, []);
    }
    insertionsAfter.get(anchorIndex).push(aligned);
  });

  const result = [];

  working.forEach((row, i) => {
    result.push(row);

    if (insertionsAfter.has(i)) {
      result.push(...insertionsAfter.get(i));
    }
  });

  return [...result, ...appended];
};

const reconcileWorkingWithServer = (
  working,
  persisted,
  server,
  selectedDimensions
) => {
  const persistedIds = new Set(persisted.map(r => r.id));
  const workingIds = new Set(working.map(r => r.id));
  const rowSignature = rowSignatureFor(selectedDimensions);
  const hasDimensionValue = hasAnyDimensionValue(selectedDimensions);

  const workingSignatures = new Set(
    working.filter(hasDimensionValue).map(rowSignature)
  );
  const removedGroupSignatures = new Set(
    persisted
      .filter(r => !workingIds.has(r.id))
      .filter(
        r => hasDimensionValue(r) && !workingSignatures.has(rowSignature(r))
      )
      .map(rowSignature)
  );

  const newlyLoaded = server.filter(
    r =>
      !persistedIds.has(r.id) &&
      !workingIds.has(r.id) &&
      !removedGroupSignatures.has(rowSignature(r))
  );

  return stitchNewlyLoadedIntoWorking(working, newlyLoaded, selectedDimensions);
};

const haveSameRowIds = (a, b) => {
  if (a.length !== b.length) return false;

  const ids = new Set(b.map(r => r.id));

  return a.every(r => ids.has(r.id));
};

const initialAddNewEntry = {
  isAdding: false,
  value: null,
  objectType: null
};

const noop = () => {};

export const useProjectCostOverrideDimensionHandlers = ({
  projectCurrency,
  initialSelectedDimensions = [],
  initialProjectRates = [],
  hasMore = false,
  loadMore = noop
} = {}) => {
  const [persistedDimensions, setPersistedDimensions] = useState(
    initialSelectedDimensions
  );
  const [persistedRates, setPersistedRates] = useState(initialProjectRates);

  const [selectedDimensions, setSelectedDimensions] = useState(
    persistedDimensions
  );
  const [projectRates, setProjectRates] = useState(persistedRates);
  const [addNewEntry, setAddNewEntry] = useState(initialAddNewEntry);

  const latestServerRatesRef = useRef(initialProjectRates);

  latestServerRatesRef.current = initialProjectRates;
  const latestPersistedRatesRef = useRef(persistedRates);

  latestPersistedRatesRef.current = persistedRates;
  const latestWorkingRatesRef = useRef(projectRates);

  latestWorkingRatesRef.current = projectRates;
  const latestSelectedDimensionsRef = useRef(selectedDimensions);

  latestSelectedDimensionsRef.current = selectedDimensions;

  // While a save is in flight the reconcile effect is suspended so no
  // intermediate render, refetch, or cache write can swap the working rows
  // mid-save. The fresh server snapshot is adopted explicitly via
  // adoptServerRates; the effect stays suspended until the reloaded rows have
  // propagated into initialProjectRates (matching the adopted set by id), so a
  // lagging prop can never reconcile stale rows back in.
  const savingRef = useRef(false);
  const adoptedRatesRef = useRef(null);

  const beginSave = useCallback(() => {
    savingRef.current = true;
    adoptedRatesRef.current = null;
  }, []);

  const adoptServerRates = useCallback(serverRates => {
    adoptedRatesRef.current = serverRates;
    setPersistedDimensions(latestSelectedDimensionsRef.current);
    setPersistedRates(serverRates);
    setProjectRates(serverRates);
  }, []);

  const onAddDimension = useCallback(dimension => {
    setSelectedDimensions(prev => [...prev, dimension]);
    setProjectRates(prev => addEmptyDimensionToRates(prev, dimension));
  }, []);

  const onRemoveDimension = useCallback(
    dimension => {
      const exclude = excludeById(dimension.id);
      const willBeEmpty =
        selectedDimensions.length === 1 &&
        selectedDimensions[0].id === dimension.id;

      setSelectedDimensions(prev => prev.filter(exclude));
      setProjectRates(prev =>
        willBeEmpty ? [] : stripDimensionFromAllRates(prev, dimension.id)
      );
    },
    [selectedDimensions]
  );

  const onDimensionValueChange = useCallback(
    async ({ index, dimensionId, value }) => {
      const target = projectRates[index];

      if (!target) return;

      const dimensionIndex = selectedDimensions.findIndex(
        d => d.id === dimensionId
      );

      if (hasMore) {
        await loadMore(true);
      }

      const server = latestServerRatesRef.current;
      const persisted = latestPersistedRatesRef.current;

      setProjectRates(prev =>
        migrateDimensionValue(
          reconcileWorkingWithServer(
            prev,
            persisted,
            server,
            selectedDimensions
          ),
          target,
          dimensionId,
          dimensionIndex,
          value,
          selectedDimensions
        )
      );
      setPersistedRates(server);
    },
    [hasMore, loadMore, projectRates, selectedDimensions]
  );

  const onEffectiveDateChange = useCallback((index, value) => {
    setProjectRates(prev => setEffectiveDateAt(prev, index, value));
  }, []);

  const onAmountChange = useCallback((index, value) => {
    setProjectRates(prev => setAmountAt(prev, index, value));
  }, []);

  const onCurrencyChange = useCallback((index, value) => {
    setProjectRates(prev => setCurrencyAt(prev, index, value));
  }, []);

  const onTypeChange = useCallback((index, type) => {
    setProjectRates(prev => setTypeAt(prev, index, type));
  }, []);

  const onPercentChange = useCallback((index, value) => {
    setProjectRates(prev => setPercentAt(prev, index, value));
  }, []);

  const onAddEntry = useCallback(
    (_index, entry) => {
      setProjectRates(prev => insertSubEntry(prev, entry, projectCurrency));
    },
    [projectCurrency]
  );

  const onRemoveEntry = useCallback((index, entry) => {
    setProjectRates(prev => removeAndAdjustGroup(prev, index, entry));
  }, []);

  const onAddNewEntry = useCallback(
    ({ record, dimensionIndex, index, dimensionValue }) => {
      const baseEntry = createSubEntry(
        selectedDimensions,
        projectCurrency,
        dimensionIndex,
        record
      );
      const dimensionAtIndex = selectedDimensions[dimensionIndex];
      const newEntry =
        dimensionValue?.id && dimensionAtIndex
          ? {
              ...baseEntry,
              [dimensionAtIndex.id]: {
                ...baseEntry[dimensionAtIndex.id],
                value: dimensionValue
              }
            }
          : baseEntry;

      setProjectRates(prev => insertEntryAt(prev, index + 1, newEntry));
    },
    [projectCurrency, selectedDimensions]
  );

  const onDeleteGroup = useCallback(groupId => {
    setProjectRates(prev => removeRatesByGroupId(prev, groupId));
  }, []);

  const onRemoveAll = useCallback(() => {
    const emptyDimensions = [];
    const emptyRates = [];

    savingRef.current = false;
    adoptedRatesRef.current = null;
    setSelectedDimensions(emptyDimensions);
    setPersistedDimensions(emptyDimensions);
    setProjectRates(emptyRates);
    setPersistedRates(emptyRates);
    setAddNewEntry(initialAddNewEntry);
  }, []);

  const onAddNewGroup = useCallback(() => {
    const entry = createTopLevelEntry(selectedDimensions, projectCurrency);

    setProjectRates(prev => [...prev, entry]);
  }, [projectCurrency, selectedDimensions]);

  const onAddEntryClick = useCallback(
    async (dimensionValue = null, objectType = null) => {
      if (hasMore) {
        await loadMore(true);
      }
      setAddNewEntry({ isAdding: true, value: dimensionValue, objectType });
    },
    [hasMore, loadMore]
  );

  const reset = useCallback(() => {
    savingRef.current = false;
    adoptedRatesRef.current = null;
    setSelectedDimensions(persistedDimensions);
    setProjectRates(persistedRates);
    setAddNewEntry(initialAddNewEntry);
  }, [persistedDimensions, persistedRates]);

  const getRatesForSave = useCallback(() => {
    const server = latestServerRatesRef.current;

    return {
      selectedDimensions: latestSelectedDimensionsRef.current,
      persistedRates: server,
      projectRates: reconcileWorkingWithServer(
        latestWorkingRatesRef.current,
        latestPersistedRatesRef.current,
        server,
        latestSelectedDimensionsRef.current
      )
    };
  }, []);

  const formOnChange = useMemo(
    () => ({
      onAddDimension,
      onRemoveDimension,
      onDimensionValueChange,
      onEffectiveDateChange,
      onAmountChange,
      onCurrencyChange,
      onTypeChange,
      onPercentChange,
      onAddEntry,
      onRemoveEntry,
      onAddNewEntry,
      onAddNewGroup,
      onAddEntryClick,
      onDeleteGroup,
      onRemoveAll
    }),
    [
      onAddDimension,
      onRemoveDimension,
      onDimensionValueChange,
      onEffectiveDateChange,
      onAmountChange,
      onCurrencyChange,
      onTypeChange,
      onPercentChange,
      onAddEntry,
      onRemoveEntry,
      onAddNewEntry,
      onAddNewGroup,
      onAddEntryClick,
      onDeleteGroup,
      onRemoveAll
    ]
  );

  const isDirty =
    projectRates !== persistedRates ||
    selectedDimensions !== persistedDimensions;

  useEffect(() => {
    // Suspended during a save; the post-save snapshot is adopted explicitly.
    // Stay suspended until the reloaded rows reach initialProjectRates (same
    // ids as what we adopted), then resume so a stale prop can't reconcile old
    // rows back in. Outside a save this only folds scrolled-in pages in.
    if (savingRef.current) {
      const adopted = adoptedRatesRef.current;

      if (adopted && haveSameRowIds(initialProjectRates, adopted)) {
        savingRef.current = false;
        adoptedRatesRef.current = null;
      }

      return;
    }

    if (persistedRates.length === 0) return;

    const persistedIds = new Set(persistedRates.map(r => r.id));
    const hasNewlyLoadedRows = initialProjectRates.some(
      r => !persistedIds.has(r.id)
    );

    if (!hasNewlyLoadedRows) return;

    if (projectRates === persistedRates) {
      setPersistedRates(initialProjectRates);
      setProjectRates(prev =>
        prev === persistedRates ? initialProjectRates : prev
      );

      return;
    }

    const mergedRates = reconcileWorkingWithServer(
      projectRates,
      persistedRates,
      initialProjectRates,
      selectedDimensions
    );

    setPersistedRates(initialProjectRates);
    setProjectRates(prev => (prev === projectRates ? mergedRates : prev));
  }, [initialProjectRates, persistedRates, projectRates, selectedDimensions]);

  return useMemo(
    () => ({
      selectedDimensions,
      projectRates,
      persistedRates,
      addNewEntry,
      setAddNewEntry,
      formOnChange,
      reset,
      beginSave,
      adoptServerRates,
      getRatesForSave,
      isDirty
    }),
    [
      selectedDimensions,
      projectRates,
      persistedRates,
      addNewEntry,
      setAddNewEntry,
      formOnChange,
      reset,
      beginSave,
      adoptServerRates,
      getRatesForSave,
      isDirty
    ]
  );
};

export default useProjectCostOverrideDimensionHandlers;
